import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Check,
  Edit3,
  GitBranch,
  Play,
  Plus,
  RefreshCw,
  Rocket,
  Settings2,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { api, jsonBody } from "../api";
import type { Locale } from "../i18n";
import { candidateKeys, parseRouteKey, providerLabel, routeKey, routeLabel } from "./modelPlans";

type ModelConfig = {
  id: string;
  name: string;
  providerId: string;
  deliveryMode: "local_api" | "direct";
  provider: string;
  model: string;
  fallbackModel: string;
  comparisonModels: string[];
  performance?: ModelPerformance[];
  endpointUrl: string;
  endpointPath: string;
  rules: Record<string, unknown>;
  status: string;
  version: number;
  apiKeyConfigured: boolean;
  usedByWorkers?: Array<{ id: string; label: string }>;
};
type ModelPerformance = {
  model: string;
  successes: number;
  failures: number;
  samples: number;
  successRate: number | null;
  requestCount: number;
  requestFailureRate: number | null;
  latencyEwmaMs: number;
  lastSelectedAt: string | null;
  disabledUntil: string | null;
};
type ModelProvider = {
  id: string;
  name: string;
  provider: string;
  endpointUrl: string;
  baseUrl?: string;
  apiPath?: string;
  models: Array<{ id: string }>;
  modelCount: number;
  lastSyncedAt?: string | null;
  usedBy?: Array<{ id: string; name: string }>;
};
type ProviderForm = {
  id: string | null;
  name: string;
  provider: string;
  endpointUrl: string;
  apiKey: string;
};
type Assignment = {
  configId: string | null;
  source: "assigned" | "default";
  model: string | null;
  fallbackModel?: string | null;
  name: string | null;
  version: number | null;
};
type ModelSnapshot = {
  configs: ModelConfig[];
  providers: ModelProvider[];
  defaultConfigId: string | null;
  assignments: Record<string, Assignment>;
};
type PresetForm = {
  id: string | null;
  name: string;
  providerId: string;
  model: string;
  deliveryMode: "local_api" | "direct";
  transport: string;
  timeoutMs: number;
  challengeWaitMs: number;
  maxChallengeAttempts: number;
  maxOutputTokens: number;
  candidateModels: string[];
  selectionMode: "fixed" | "auto";
  explorationPercent: number;
  latencyTargetMs: number;
  failureCooldownMs: number;
  endpointUrl: string;
  endpointPath: string;
  apiKey: string;
};
type PresetPayload = {
  name: string;
  providerId?: string;
  model: string;
  deliveryMode: "local_api" | "direct";
  comparisonModels: string[];
  fallbackModel: string;
  endpointUrl: string;
  endpointPath: string;
  rules: Record<string, unknown>;
  apiKey?: string;
};

const copy = {
  en: {
    title: "Models",
    subtitle: "Each Agent uses one preset. Automatic routing chooses among the models selected in that preset, across providers.",
    manage: "Manage models",
    close: "Close",
    currentDefault: "Current default",
    providers: "Provider catalogs",
    library: "Runtime presets",
    sync: "Sync",
    lastSynced: "Last synced",
    neverSynced: "Not synced yet",
    test: "Test",
    newPreset: "New preset",
    newProvider: "Add provider",
    editProvider: "Edit provider",
    providerName: "Provider name",
    providerProtocol: "API protocol",
    saveProvider: "Save provider",
    providerAdded: "Provider added. Sync its catalog to load models.",
    providerUpdated: "Provider updated.",
    providerApiOutdated: "The API server has not loaded provider management changes. Restart the TopCaptcha API and save again; your input is preserved.",
    keepProviderKey: "Leave blank to keep the current key.",
    providerEndpoint: "API endpoint URL",
    providerEndpointHint: "Enter the API endpoint URL. OpenAI Chat/Responses compatible providers usually require a URL ending in /v1 (for example, https://api.openai.com/v1); Anthropic Messages / Gemini Native depend on the upstream provider.",
    delete: "Delete",
    deleteProvider: "Delete provider",
    deletePreset: "Delete preset",
    confirmDeleteProvider: "Delete this provider?",
    confirmDeletePreset: "Delete this preset?",
    providerInUse: "Used by presets; remove those references before deleting:",
    presetInUse: "Used by Agents; switch them to another preset before deleting:",
    deleted: "Deleted.",
    edit: "Edit",
    presetName: "Preset name",
    primary: "Primary model",
    fallback: "Fallback model",
    noFallback: "No fallback",
    chooseProvider: "Choose provider",
    chooseModel: "Choose model",
    runtime: "Runtime",
    timeout: "Request timeout (ms)",
    challengeWait: "Challenge wait grace (ms)",
    challengeWaitHint: "How long the Agent waits for the image challenge or token to appear. Does not change model request or task timeout.",
    attempts: "Challenge attempts",
    maxOutputTokens: "Max output tokens",
    routing: "Model routing",
    scope: "Model range",
    scopeHint: "Fixed mode uses the baseline model. Automatic mode sends traffic to every enabled model in this range and shifts toward the model with the best task success, latency, and reliability.",
    anchor: "Baseline model",
    rangeModels: "Models in range",
    advancedFallback: "Cross-provider recovery",
    fixed: "Fixed",
    automatic: "Automatic",
    fixedHint: "Fixed mode uses only the baseline model. Other selected models take traffic when automatic routing is enabled.",
    automaticHint: "Thompson Sampling selects from all enabled candidates using task success, latency, request failures, and exploration traffic.",
    candidates: "Automatic candidates",
    candidatesHint: "Select models from any provider. The baseline is included in the selected count. Maximum: 11 models.",
    selectedModels: "Selected models",
    filterModels: "Search model or provider",
    noMatches: "No matching models",
    exploration: "Exploration traffic (%)",
    latencyTarget: "Latency target (ms)",
    failureCooldown: "Failure cooldown (ms)",
    performance: "Current model performance",
    noSamples: "No samples",
    taskSuccessRate: "Task success rate",
    enableAutomatic: "Enable automatic",
    disableAutomatic: "Disable automatic",
    fixedRouting: "Fixed routing",
    automaticRouting: "Automatic routing",
    candidatesEnabled: "selected models",
    primaryModel: "Primary",
    fallbackModelLabel: "Fallback",
    delivery: "Delivery",
    local: "TopCaptcha proxy",
    direct: "Direct provider",
    transport: "Transport",
    chat: "Chat completions",
    native: "Native Gemini",
    advanced: "Advanced delivery and credentials",
    endpoint: "Base URL",
    path: "Path",
    key: "API key",
    applyAll: "Apply to every authorized Worker",
    saveAll: "Save & apply to all Workers",
    saveDefault: "Save & set default",
    cancel: "Cancel",
    setDefault: "Set default",
    publish: "Enable & apply",
    archive: "Archive",
    default: "Default",
    active: "Enabled",
    draft: "Draft",
    archived: "Archived",
    ready: "ready",
    notReady: "not ready",
    testResult: "Test result",
    error: "Unable to load model control data.",
    saved: "Model preset applied.",
    noModels: "No model presets yet.",
    tabs: "Model management views",
    editPreset: "Edit preset",
    back: "Back to presets",
    libraryHint: "Create multiple presets. Each Agent follows the default or a manually assigned preset; automatic routing selects models within it.",
    providerHint: "Model catalogs refresh automatically in the background. You can also sync now or test a model; Worker assignments stay unchanged.",
    applyHint: "Also replaces individual model assignments on all authorized Workers.",
    defaultHint: "Sets the default without reassigning Workers. Workers already using this preset receive updates.",
    models: "models",
    emptyProviders: "No providers configured.",
    testingModel: "Model to test",
    searchModels: "Search models",
    primaryHint: "Required baseline for requests and statistics.",
    fallbackHint: "Optional recovery model outside the main range.",
  },
  "zh-CN": {
    title: "模型管理",
    subtitle: "每台 Agent 使用一个运行方案；自动路由在方案内选中的模型之间选择，支持跨供应商。",
    manage: "管理模型",
    close: "关闭",
    currentDefault: "当前默认",
    providers: "供应商目录",
    library: "运行方案",
    sync: "同步",
    lastSynced: "上次同步",
    neverSynced: "尚未同步",
    test: "测试",
    newPreset: "新建方案",
    newProvider: "新增供应商",
    editProvider: "编辑供应商",
    providerName: "供应商名称",
    providerProtocol: "接口协议",
    saveProvider: "保存供应商",
    providerAdded: "供应商已新增，请同步模型目录。",
    providerUpdated: "供应商已更新。",
    providerApiOutdated: "后台尚未加载供应商管理更新，请重启 TopCaptcha API 后再保存；当前输入已保留。",
    keepProviderKey: "留空保留现有密钥",
    providerEndpoint: "API 端点地址",
    providerEndpointHint: "填写 API 端点地址。OpenAI Chat/Responses 兼容渠道通常需以 /v1 结尾（例如 https://api.openai.com/v1）；Anthropic Messages / Gemini Native 视上游而定。",
    delete: "删除",
    deleteProvider: "删除供应商",
    deletePreset: "删除方案",
    confirmDeleteProvider: "确认删除此供应商？",
    confirmDeletePreset: "确认删除此方案？",
    providerInUse: "以下方案正在使用，请先移除引用再删除：",
    presetInUse: "以下 Agent 正在使用，请先切换到其他方案再删除：",
    deleted: "已删除。",
    edit: "编辑",
    presetName: "方案名称",
    primary: "主模型",
    fallback: "备用模型",
    noFallback: "不使用备用",
    chooseProvider: "选择供应商",
    chooseModel: "选择模型",
    runtime: "运行参数",
    timeout: "请求超时（毫秒）",
    challengeWait: "图片挑战等待宽限（毫秒）",
    challengeWaitHint: "控制 Agent 等待图片挑战或 token 出现的时间，不会改变模型请求超时或任务总超时。",
    attempts: "挑战尝试次数",
    maxOutputTokens: "输出 token 上限",
    routing: "模型选择",
    scope: "模型范围",
    scopeHint: "固定模式只使用基础模型；自动模式会把流量发给范围内已启用的模型，并向任务成功率更高、延迟更低、更稳定的模型倾斜。",
    anchor: "基础模型",
    rangeModels: "范围内模型",
    advancedFallback: "跨供应商兜底",
    fixed: "固定",
    automatic: "自动",
    fixedHint: "固定模式只使用基础模型；其他已选模型在启用自动路由后参与流量。",
    automaticHint: "用 Thompson Sampling 根据任务成功率、延迟、请求失败率和探索流量自动选择候选模型。",
    candidates: "自动候选",
    candidatesHint: "可跨供应商勾选模型；基础模型也计入已选数量，最多 11 个。",
    selectedModels: "已选模型",
    filterModels: "搜索模型或供应商",
    noMatches: "没有匹配的模型",
    exploration: "探索流量（%）",
    performance: "当前模型表现",
    noSamples: "暂无样本",
    taskSuccessRate: "任务成功率",
    latencyTarget: "延迟目标（毫秒）",
    failureCooldown: "失败熔断（毫秒）",
    enableAutomatic: "启用自动",
    disableAutomatic: "停用自动",
    fixedRouting: "固定路由",
    automaticRouting: "自动路由",
    candidatesEnabled: "个已选模型",
    primaryModel: "主模型",
    fallbackModelLabel: "备用",
    delivery: "下发方式",
    local: "TopCaptcha 代理",
    direct: "直连供应商",
    transport: "传输方式",
    chat: "Chat completions",
    native: "Gemini 原生",
    advanced: "高级下发和凭据",
    endpoint: "基础地址",
    path: "路径",
    key: "API key",
    applyAll: "应用到所有已授权 Worker",
    saveAll: "保存并应用到全部 Worker",
    saveDefault: "保存并设为默认",
    cancel: "取消",
    setDefault: "设为默认",
    publish: "启用并应用",
    archive: "归档",
    default: "默认",
    active: "已启用",
    draft: "草稿",
    archived: "已归档",
    ready: "可用",
    notReady: "不可用",
    testResult: "测试结果",
    error: "无法加载模型管理数据。",
    saved: "模型方案已应用。",
    noModels: "还没有模型方案。",
    tabs: "模型管理视图",
    editPreset: "编辑方案",
    back: "返回方案列表",
    libraryHint: "可以创建多个方案；每台 Agent 跟随默认或手动指定一个方案，自动路由在该方案内部选模型。",
    providerHint: "模型列表由后台定时自动同步，也可以立即同步或测试指定模型；现有 Worker 的模型分配保持不变。",
    applyHint: "同时覆盖所有已授权 Worker 的单独模型分配。",
    defaultHint: "更新默认方案，不更改单独分配；已使用本方案的 Worker 会同步更新。",
    models: "个模型",
    emptyProviders: "尚未配置供应商。",
    testingModel: "测试模型",
    searchModels: "搜索模型",
    primaryHint: "请求和统计的必选基础模型。",
    fallbackHint: "主范围之外的可选兜底模型。",
  },
};

const DEFAULT_MAX_OUTPUT_TOKENS = 4000;
const MAX_MAX_OUTPUT_TOKENS = 16384;

function normalizeMaxOutputTokens(value: unknown): number {
  return typeof value === "number" && Number.isSafeInteger(value)
    ? Math.max(DEFAULT_MAX_OUTPUT_TOKENS, Math.min(value, MAX_MAX_OUTPUT_TOKENS))
    : DEFAULT_MAX_OUTPUT_TOKENS;
}

const emptyForm: PresetForm = {
  id: null,
  name: "",
  providerId: "",
  model: "",
  deliveryMode: "local_api",
  transport: "chat",
  timeoutMs: 45000,
  challengeWaitMs: 20000,
  maxChallengeAttempts: 5,
  maxOutputTokens: DEFAULT_MAX_OUTPUT_TOKENS,
  candidateModels: [],
  selectionMode: "fixed",
  explorationPercent: 8,
  latencyTargetMs: 30000,
  failureCooldownMs: 90000,
  endpointUrl: "",
  endpointPath: "/",
  apiKey: "",
};

function preferredModel(provider?: ModelProvider, ...preferred: string[]) {
  const models = provider?.models || [];
  for (const name of preferred) {
    const found = models.find((model) => model.id === name);
    if (found) return found.id;
  }
  return models[0]?.id || "";
}

function providerWithModel(providers: ModelProvider[], ...preferred: string[]) {
  for (const name of preferred) {
    const provider = providers.find((item) =>
      item.models.some((model) => model.id === name),
    );
    if (provider) return provider;
  }
  return providers[0];
}

function presetLabel(config: ModelConfig, providers: ModelProvider[] = []) {
  const peers = candidateCount(config) - 1;
  const label = `${config.model} · ${providerLabel(config.providerId, providers)}`;
  return config.rules?.selectionMode === "auto" && peers > 0
    ? `${label} +${peers} · 自动`
    : label;
}

function routingEnabled(config: ModelConfig) {
  return config.rules?.selectionMode === "auto" && config.deliveryMode === "local_api";
}

function candidateCount(config: ModelConfig) {
  return candidateKeys(config).length;
}

function currentPerformance(config: ModelConfig) {
  const selected = candidateKeys(config);
  const models = routingEnabled(config) ? selected : selected.slice(0, 1);
  return models.map(model => ({
    model,
    stats: model.includes("||") ? config.performance?.find(item => item.model === model) : undefined,
  }));
}

export function ModelControl({ locale }: { locale: Locale }) {
  const t = copy[locale];
  const [snapshot, setSnapshot] = useState<ModelSnapshot>({
    configs: [],
    providers: [],
    defaultConfigId: null,
    assignments: {},
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [view, setView] = useState<"library" | "providers">("library");
  const dialogRef = useRef<HTMLElement | null>(null);
  const [form, setForm] = useState<PresetForm | null>(null);
  const [providerForm, setProviderForm] = useState<ProviderForm | null>(null);
  const [applyAll, setApplyAll] = useState(true);
  const [testModels, setTestModels] = useState<Record<string, string>>({});
  const [testSearch, setTestSearch] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [modelSearch, setModelSearch] = useState("");

  const primaryProvider = snapshot.providers.find(
    (provider) => provider.id === form?.providerId,
  );
  const defaultConfig = snapshot.configs.find(
    (config) => config.id === snapshot.defaultConfigId,
  );

  const load = useCallback(async () => {
    try {
      const result = await api<ModelSnapshot>("/api/admin/model-configs");
      setSnapshot({
        configs: result.configs || [],
        providers: result.providers || [],
        defaultConfigId: result.defaultConfigId || null,
        assignments: result.assignments || {},
      });
    } catch {
      setNotice(t.error);
    }
  }, [t.error]);

  useEffect(() => {
    queueMicrotask(() => void load());
  }, [load]);

  useEffect(() => {
    if (!panelOpen) return;
    queueMicrotask(() => void load());
    const timer = setInterval(() => void load(), 60_000);
    return () => clearInterval(timer);
  }, [panelOpen, load]);

  useEffect(() => {
    if (!panelOpen) return;
    const opener = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPanelOpen(false);
      if (event.key !== "Tab") return;
      const controls = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), summary, [tabindex="0"]',
      ) || []).filter(element => element.getClientRects().length && !element.closest('[hidden]') && element.tabIndex >= 0);
      const first = controls[0], last = controls.at(-1);
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
        event.preventDefault(); first.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = overflow;
      opener?.focus();
    };
  }, [panelOpen]);

  const newPreset = () => {
    const primary = providerWithModel(snapshot.providers, "gemini-3.8-flash-low", "gemini-3-flash");
    setForm({
      ...emptyForm,
      name: locale === "en" ? "Production preset" : "生产方案",
      providerId: primary?.id || "",
      model: preferredModel(primary, "gemini-3.8-flash-low", "gemini-3-flash"),
      transport: primary?.provider === "gemini" ? "gemini" : "chat",
      candidateModels: [],
      selectionMode: "fixed",
      explorationPercent: 8,
    });
    setView("library");
    setApplyAll(true);
    setNotice("");
  };
  const cancelEdit = () => {
    setForm(null);
    setNotice("");
  };

  const sync = async (provider: ModelProvider) => {
    setBusy(true);
    try {
      const result = await api<{ models: ModelProvider["models"]; elapsedMs: number; warnings?: string[] }>(
        `/api/admin/model-providers/${provider.id}/sync`,
        { method: "POST", body: "{}" },
      );
      setTestModels(current => ({
        ...current,
        [provider.id]: result.models.some(model => model.id === current[provider.id]) ? current[provider.id] : result.models[0]?.id || "",
      }));
      setNotice((locale === "en"
          ? `Models synced: ${result.models.length} in ${result.elapsedMs}ms`
          : `模型已同步：${result.models.length} 个，${result.elapsedMs}ms`) + (result.warnings?.length ? ` · ${result.warnings.join(" · ")}` : ""));
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const availableTestModels = (provider: ModelProvider) => provider.models.filter(model => model.id.toLowerCase().includes((testSearch[provider.id] || "").toLowerCase()));
  const selectedTestModel = (provider: ModelProvider) => {
    const models = availableTestModels(provider);
    return models.find(model => model.id === testModels[provider.id])?.id || models[0]?.id || "";
  };
  const testProvider = async (provider: ModelProvider) => {
    const model = selectedTestModel(provider);
    if (!model) return;
    setBusy(true);
    setTestResult("");
    try {
      const response = await api<{
        result: {
          model: string;
          httpStatus: number;
          usable?: boolean;
          finishReason?: string | null;
          elapsedMs: number;
          qualityScore: number;
          error: string | null;
        };
      }>(`/api/admin/model-providers/${provider.id}/test`, {
        method: "POST",
        body: jsonBody({ model }),
      });
      const result = response.result;
      setTestResult(
        `${t.testResult}: ${result.model} · HTTP ${result.httpStatus} · ${result.elapsedMs}ms · ${result.qualityScore}/100 · ${result.usable === false ? t.notReady : t.ready}${result.finishReason ? ` · ${result.finishReason}` : ""}${result.error ? ` · ${result.error}` : ""}`,
      );
    } catch (error) {
      setTestResult(error instanceof Error ? error.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const editConfig = (config: ModelConfig) => {
    setForm({
      id: config.id,
      name: config.name,
      providerId: config.providerId,
      model: config.model,
      deliveryMode: config.deliveryMode,
      transport: String(config.rules?.transport || "chat"),
      timeoutMs: Number(config.rules?.timeoutMs || 45000),
      challengeWaitMs: Number(config.rules?.challengeWaitMs || 20000),
      maxChallengeAttempts: Number(config.rules?.maxChallengeAttempts || 5),
      maxOutputTokens: normalizeMaxOutputTokens(config.rules?.maxOutputTokens),
      candidateModels: candidateKeys(config).slice(1),
      selectionMode: config.rules?.selectionMode === "auto" ? "auto" : "fixed",
      explorationPercent: Number(config.rules?.explorationPercent ?? 8),
      latencyTargetMs: Number(config.rules?.latencyTargetMs ?? 30000),
      failureCooldownMs: Number(config.rules?.failureCooldownMs ?? 90000),
      endpointUrl: config.endpointUrl,
      endpointPath: config.endpointPath,
      apiKey: "",
    });
    setPanelOpen(true);
    setView("library");
    setNotice("");
  };

  const changePrimaryProvider = (providerId: string) => {
    const provider = snapshot.providers.find((item) => item.id === providerId);
    setForm((current) =>
      current
        ? {
            ...current,
            providerId,
            model: preferredModel(
              provider,
              "gemini-3.8-flash-low",
              "gemini-3-flash",
              current.model,
            ),
            transport: provider?.provider === "gemini" ? "gemini" : "chat",
            endpointUrl: provider?.endpointUrl || current.endpointUrl,
            endpointPath: "/",
            candidateModels: current.candidateModels.filter(key => key !== routeKey(providerId, preferredModel(provider, "gemini-3.8-flash-low", "gemini-3-flash", current.model))),
          }
        : current,
    );
  };

  const payloadFromForm = (value: PresetForm): PresetPayload => ({
    name: value.name.trim() || value.model,
    providerId: value.providerId || undefined,
    model: value.model,
    deliveryMode: value.deliveryMode,
    comparisonModels: value.candidateModels.filter(Boolean).slice(0, 10),
    fallbackModel: "",
    endpointUrl: value.endpointUrl,
    endpointPath: value.endpointPath,
    rules: {
      captchaMode: "auto",
      transport: value.transport,
      timeoutMs: value.timeoutMs,
      challengeWaitMs: value.challengeWaitMs,
      maxChallengeAttempts: value.maxChallengeAttempts,
      maxOutputTokens: normalizeMaxOutputTokens(value.maxOutputTokens),
      selectionMode: value.selectionMode,
      explorationPercent: value.explorationPercent,
      latencyTargetMs: value.latencyTargetMs,
      failureCooldownMs: value.failureCooldownMs,
      fallbackProviderId: "",
    },
    ...(value.apiKey ? { apiKey: value.apiKey } : {}),
  });

  const payloadFromConfig = (
    config: ModelConfig,
    overrides: { selectionMode?: "fixed" | "auto"; comparisonModels?: string[] } = {},
  ): PresetPayload => ({
    name: config.name,
    providerId: config.providerId || undefined,
    model: config.model,
    deliveryMode: config.deliveryMode,
    comparisonModels: overrides.comparisonModels ?? candidateKeys(config).slice(1),
    fallbackModel: "",
    endpointUrl: config.endpointUrl,
    endpointPath: config.endpointPath,
    rules: {
      ...config.rules,
      selectionMode: overrides.selectionMode ?? (config.rules?.selectionMode === "auto" ? "auto" : "fixed"),
      fallbackProviderId: "",
    },
  });

  const applyPreset = async (presetId: string, input: PresetPayload, allWorkers: boolean) => {
    return api<{
      config: ModelConfig;
      delivered: string[];
      failed: Array<{ workerId: string; message: string }>;
    }>(`/api/admin/model-configs/${presetId}/apply`, {
      method: "POST",
      body: jsonBody({ config: input, allWorkers }),
    });
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setNotice("");
    try {
      const input = payloadFromForm(form);
      let presetId = form.id;
      if (!presetId) {
        const created = await api<{ config: ModelConfig }>(
          "/api/admin/model-configs",
          { method: "POST", body: jsonBody(input) },
        );
        presetId = created.config.id;
      }
      const result = await applyPreset(presetId, input, applyAll);
      const failed = result.failed?.length || 0;
      const delivered = result.delivered?.length || 0;
      setNotice(
        `${t.saved} v${result.config.version} · ${locale === "en" ? "Workers" : "Worker"} ${delivered}/${delivered + failed}${failed ? ` · ${failed} failed` : ""}`,
      );
      setForm(null);
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const setDefault = async (config: ModelConfig) => {
    setBusy(true);
    try {
      await api("/api/admin/model-configs/default", {
        method: "PATCH",
        body: jsonBody({ configId: config.id }),
      });
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const applyExisting = async (config: ModelConfig) => {
    setBusy(true);
    try {
      const input = payloadFromConfig(config);
      await applyPreset(config.id, input, true);
      await load();
      setNotice(t.saved);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const updateRouting = async (
    config: ModelConfig,
    overrides: { selectionMode?: "fixed" | "auto"; comparisonModels?: string[] },
  ) => {
    setBusy(true);
    setNotice("");
    try {
      await api(`/api/admin/model-configs/${encodeURIComponent(config.id)}`, {
        method: "PATCH",
        body: jsonBody(payloadFromConfig(config, overrides)),
      });
      const result = await api<{ config: ModelConfig; delivered: string[]; failed: Array<{ workerId: string }> }>(
        `/api/admin/model-configs/${encodeURIComponent(config.id)}/publish`,
        { method: "POST", body: "{}" },
      );
      const failed = result.failed?.length || 0;
      const delivered = result.delivered?.length || 0;
      setNotice(
        locale === "en"
          ? `Routing updated v${result.config.version} · Workers ${delivered}/${delivered + failed}`
          : `路由已更新 v${result.config.version} · Worker ${delivered}/${delivered + failed}`,
      );
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const archive = async (config: ModelConfig) => {
    setBusy(true);
    try {
      await api(`/api/admin/model-configs/${config.id}/archive`, {
        method: "POST",
        body: "{}",
      });
      await load();
    } catch (error) {
      setNotice(error instanceof Error ? error.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const editProvider = (provider: ModelProvider) => {
    setNotice("");
    setProviderForm({ id: provider.id, name: provider.name, provider: provider.provider,
      endpointUrl: (provider.endpointUrl || `${(provider.baseUrl || "").replace(/\/+$/, "")}/${(provider.apiPath || "/").replace(/^\/+/, "")}`).replace(/\/+$/, ""),
      apiKey: "" });
  };

  const saveProvider = async (event: FormEvent) => {
    event.preventDefault();
    if (!providerForm || busy) return;
    setBusy(true);
    setNotice("");
    try {
      const { id, endpointUrl, ...input } = providerForm;
      await api(id ? `/api/admin/model-providers/${encodeURIComponent(id)}` : "/api/admin/model-providers", {
        method: id ? "PATCH" : "POST", body: jsonBody({ ...input, baseUrl: endpointUrl.trim(), apiPath: "/" }),
      });
      setProviderForm(null);
      setNotice(id ? t.providerUpdated : t.providerAdded);
      await load();
    } catch (error) {
      const outdated = error instanceof Error && "status" in error && error.status === 404 && "code" in error && error.code === "NOT_FOUND";
      setNotice(outdated ? t.providerApiOutdated : error instanceof Error ? error.message : t.error);
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async (kind: "provider" | "preset", entry: { id: string; name: string }) => {
    if (busy || !window.confirm(`${kind === "provider" ? t.confirmDeleteProvider : t.confirmDeletePreset}\n${entry.name}`)) return;
    setBusy(true);
    setNotice("");
    try {
      await api(`/api/admin/${kind === "provider" ? "model-providers" : "model-configs"}/${encodeURIComponent(entry.id)}`, { method: "DELETE" });
      setNotice(t.deleted);
      await load();
    } catch (error) {
      const code = error instanceof Error && "code" in error ? error.code : "";
      setNotice(code === "MODEL_PROVIDER_IN_USE" ? t.providerInUse
        : code === "MODEL_CONFIG_IN_USE" ? t.presetInUse
        : error instanceof Error ? error.message : t.error);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const providerReferences = (provider: ModelProvider) => provider.usedBy ?? snapshot.configs
    .filter(config => candidateKeys(config).some(key => parseRouteKey(key, config.providerId).providerId === provider.id))
    .map(config => ({ id: config.id, name: config.name }));
  const presetWorkers = (config: ModelConfig) => config.usedByWorkers ?? Object.entries(snapshot.assignments)
    .filter(([, assignment]) => assignment.configId === config.id)
    .map(([id]) => ({ id, label: id }));

  const providerSelect = (
    value: string,
    onChange: (value: string) => void,
    allowEmpty: boolean,
  ) => (
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {allowEmpty && <option value="">{t.chooseProvider}</option>}
      {snapshot.providers.map((provider) => (
        <option key={provider.id} value={provider.id}>
          {provider.name}
        </option>
      ))}
    </select>
  );

  const toggleCandidate = (model: string, enabled: boolean) => {
    setForm((current) => current ? {
      ...current,
      candidateModels: enabled
        ? [...new Set([...current.candidateModels, model])].slice(0, 10)
        : current.candidateModels.filter((item) => item !== model)
    } : current);
  };

  const rate = (value: number | null | undefined) =>
    typeof value === "number" && Number.isFinite(value) ? `${Math.round(value * 1000) / 10}%` : "—";

  return (
    <section className="model-control" aria-label={t.title}>
      <header className="model-control-head">
        <div>
          <h3>
            <GitBranch size={17} />
            {t.title}
          </h3>
          <p>{t.subtitle}</p>
        </div>
        <button className="button button-primary" onClick={() => setPanelOpen(true)}>
          <Settings2 size={15} />
          {t.manage}
        </button>
      </header>
      <div className="model-control-summary">
        <span>{t.currentDefault}</span>
        <strong>{defaultConfig ? presetLabel(defaultConfig, snapshot.providers) : "—"}</strong>
        <small>
          {defaultConfig ? `${defaultConfig.name} · v${defaultConfig.version}` : ""}
        </small>
      </div>
      {notice && !panelOpen && (
        <div className="alert" role="status">
          {notice}
        </div>
      )}

      {panelOpen && createPortal(
        <div
          className="model-modal-scrim"
          role="presentation"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) setPanelOpen(false);
          }}
        >
          <section ref={dialogRef} tabIndex={-1} className="model-modal model-workspace" role="dialog" aria-modal="true" aria-label={t.title}>
            <header className="model-modal-head">
              <div>
                <h3>
                  <GitBranch size={17} />
                  {t.title}
                </h3>
                <p>{t.subtitle}</p>
              </div>
              <div className="model-modal-head-actions">
                <button className="button button-ghost" aria-label={t.close} onClick={() => setPanelOpen(false)}>
                  <X size={17} />
                </button>
              </div>
            </header>
            <div className="model-workspace-tabs" role="tablist" aria-label={t.tabs}>
              {(["library", "providers"] as const).map(tab => (
                <button key={tab} id={`models-tab-${tab}`} role="tab" aria-selected={view === tab}
                  aria-controls={`models-panel-${tab}`} tabIndex={view === tab ? 0 : -1}
                  onClick={() => setView(tab)}
                  onKeyDown={event => {
                    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
                    event.preventDefault();
                    const next = event.key === "Home" ? "library" : event.key === "End" ? "providers" : tab === "library" ? "providers" : "library";
                    setView(next);
                    document.getElementById(`models-tab-${next}`)?.focus();
                  }}>
                  {tab === "library" ? t.library : t.providers}
                  <span>{tab === "library" ? snapshot.configs.length : snapshot.providers.length}</span>
                </button>
              ))}
            </div>
            <div className="model-modal-body">
              {notice && (
                <div className="alert" role="status">
                  {notice}
                </div>
              )}

              <section hidden={view !== "providers"} id="models-panel-providers" role="tabpanel" aria-labelledby="models-tab-providers" className="model-provider-catalog">
                <div className="model-section-heading">
                  <div>
                    <h4>{t.providers}</h4>
                    <p>
                      {t.providerHint}
                    </p>
                  </div>
                  <button className="button button-primary" disabled={busy || providerForm !== null} onClick={() => { setNotice(""); setProviderForm({ id: null, name: "", provider: "openai", endpointUrl: "", apiKey: "" }); }}><Plus size={15} />{t.newProvider}</button>
                </div>
                {providerForm && <form className="model-provider-form" onSubmit={saveProvider}>
                  <h5>{providerForm.id ? t.editProvider : t.newProvider}</h5>
                  <fieldset disabled={busy}>
                    <label>{t.providerName}<input value={providerForm.name} maxLength={80} required onChange={event => setProviderForm({ ...providerForm, name: event.target.value })} /></label>
                    <label>{t.providerProtocol}<select value={providerForm.provider} onChange={event => setProviderForm({ ...providerForm, provider: event.target.value })}><option value="openai">OpenAI compatible</option><option value="gemini">Gemini</option><option value="http">HTTP</option></select></label>
                    <label className="model-provider-endpoint">{t.providerEndpoint}<input type="url" value={providerForm.endpointUrl} placeholder="https://api.openai.com/v1" aria-describedby="model-provider-endpoint-hint" required onChange={event => setProviderForm({ ...providerForm, endpointUrl: event.target.value })} /></label>
                    <p id="model-provider-endpoint-hint" className="model-provider-endpoint">{t.providerEndpointHint}</p>
                    <label>{t.key}<input type="password" autoComplete="new-password" value={providerForm.apiKey} placeholder={providerForm.id ? t.keepProviderKey : ""} required={!providerForm.id} onChange={event => setProviderForm({ ...providerForm, apiKey: event.target.value })} /></label>
                  </fieldset>
                  <div className="model-provider-form-actions"><button type="button" className="button button-ghost" disabled={busy} onClick={() => setProviderForm(null)}>{t.cancel}</button><button type="submit" className="button button-primary" disabled={busy}><Check size={15} />{t.saveProvider}</button></div>
                </form>}
                <div className="model-provider-grid">
                  {snapshot.providers.map((provider) => {
                    const references = providerReferences(provider);
                    const blocked = references.length ? `${t.providerInUse} ${references.map(config => config.name).join(", ")}` : "";
                    return (
                    <article key={provider.id} className="model-provider-row">
                      <div>
                        <strong>{provider.name}</strong>
                        <small>
                          {provider.provider} · {provider.modelCount} {t.models}
                        </small>
                        <small>{t.lastSynced} · {provider.lastSyncedAt ? new Date(provider.lastSyncedAt).toLocaleString(locale) : t.neverSynced}</small>
                        {blocked && <small className="model-delete-hint">{blocked}</small>}
                      </div>
                      <div className="model-provider-test-options">
                      {provider.models.length > 50 && <input type="search" aria-label={`${provider.name} ${t.searchModels}`} placeholder={t.searchModels} value={testSearch[provider.id] || ""} onChange={event => setTestSearch(current => ({ ...current, [provider.id]: event.target.value }))} />}
                      <label className="model-provider-selector">{t.testingModel}<select
                        aria-label={`${provider.name} model`}
                        value={selectedTestModel(provider)}
                        onChange={(event) =>
                          setTestModels((current) => ({
                            ...current,
                            [provider.id]: event.target.value,
                          }))
                        }
                      >
                        {availableTestModels(provider).map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.id}
                          </option>
                        ))}
                      </select></label>
                      </div>
                      <div className="model-provider-actions">
                      <button className="button button-ghost" disabled={busy || providerForm !== null} aria-label={`${t.editProvider} ${provider.name}`} onClick={() => editProvider(provider)}><Edit3 size={13} />{t.edit}</button>
                      <button className="button button-ghost" disabled={busy} onClick={() => void sync(provider)}>
                        <RefreshCw size={13} />
                        {t.sync}
                      </button>
                      <button
                        className="button button-outline"
                        disabled={busy || !selectedTestModel(provider)}
                        onClick={() => void testProvider(provider)}
                      >
                        <Play size={13} />
                        {t.test}
                      </button>
                      <button className="button button-ghost model-delete-button" disabled={busy || references.length > 0} title={blocked || `${t.deleteProvider} ${provider.name}`} aria-label={`${t.deleteProvider} ${provider.name}`} onClick={() => void deleteEntry("provider", provider)}><Trash2 size={13} />{t.delete}</button>
                      </div>
                    </article>
                  ); })}
                </div>
                {!snapshot.providers.length && <p className="model-config-empty">{t.emptyProviders}</p>}
                {testResult && (
                  <div className="alert" role="status">
                    {testResult}
                  </div>
                )}
              </section>

              <section hidden={view !== "library"} id="models-panel-library" role="tabpanel" aria-labelledby="models-tab-library">
              {form && (
                <form className="model-preset-form" onSubmit={submit}>
                  <div className="model-editor-heading">
                    <button type="button" className="button button-ghost" disabled={busy} onClick={cancelEdit}><ArrowLeft size={15} />{t.back}</button>
                    <h4>{form.id ? t.editPreset : t.newPreset}</h4>
                  </div>
                  <label className="model-preset-name">
                    {t.presetName}
                    <input
                      value={form.name}
                      onChange={(event) => setForm({ ...form, name: event.target.value })}
                      required
                    />
                  </label>

                  <section className="model-scope-card">
                    <div className="model-scope-header">
                      <div>
                        <h4>{t.scope}</h4>
                        <p>{t.scopeHint}</p>
                      </div>
                      <div className="model-routing-switch" role="radiogroup" aria-label={t.routing}>
                        <button type="button" role="radio" aria-checked={form.selectionMode === "fixed"} className={form.selectionMode === "fixed" ? "active" : ""} onClick={() => setForm({ ...form, selectionMode: "fixed" })}>{t.fixed}</button>
                        <button type="button" role="radio" aria-checked={form.selectionMode === "auto"} disabled={form.deliveryMode !== "local_api"} className={form.selectionMode === "auto" ? "active" : ""} onClick={() => setForm({ ...form, selectionMode: "auto" })}>{t.automatic}</button>
                      </div>
                    </div>
                    <div className="model-scope-base">
                      <label>
                        {locale === "en" ? "Provider" : "供应商"}
                        {providerSelect(form.providerId, changePrimaryProvider, false)}
                      </label>
                      <label>
                        {t.anchor}
                        <select
                          value={form.model}
                          required
                          onChange={(event) => setForm({ ...form, model: event.target.value, candidateModels: form.candidateModels.filter(item => item !== routeKey(form.providerId, event.target.value)) })}
                        >
                          <option value="">{t.chooseModel}</option>
                          {(primaryProvider?.models || []).map((model) => (
                            <option key={model.id} value={model.id}>
                              {model.id} · {primaryProvider?.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div className="model-selected-range" aria-label={t.selectedModels}>
                      <strong>{t.selectedModels} · {candidateKeys({ ...form, comparisonModels: form.candidateModels }).length}</strong>
                      <div className="model-selected-chips">
                        {candidateKeys({ ...form, comparisonModels: form.candidateModels }).map((key, index) => <span key={key}>{routeLabel(key, snapshot.providers)}{index === 0 ? <small>{t.anchor}</small> : <button type="button" className="model-chip-remove" aria-label={`${locale === "en" ? "Remove" : "移除"} ${routeLabel(key, snapshot.providers)}`} onClick={() => toggleCandidate(key, false)}><X size={12} /></button>}</span>)}
                      </div>
                    </div>
                    <input type="search" aria-label={t.filterModels} placeholder={t.filterModels} value={modelSearch} onChange={event => setModelSearch(event.target.value)} />
                    <div className="model-candidate-list" aria-label={t.rangeModels}>
                      {snapshot.providers.flatMap(provider => provider.models.map(model => ({ provider, model }))).filter(({ provider, model }) => `${provider.name} ${model.id}`.toLowerCase().includes(modelSearch.toLowerCase())).map(({ provider, model }) => {
                        const key = routeKey(provider.id, model.id);
                        const checked = form.candidateModels.includes(key);
                        const isAnchor = provider.id === form.providerId && model.id === form.model;
                        return (
                          <label key={key} className={checked || isAnchor ? "active" : ""}>
                            <input type="checkbox" checked={checked || isAnchor} disabled={isAnchor || (!checked && form.candidateModels.length >= 10) || (form.deliveryMode === "direct" && provider.id !== form.providerId)} onChange={(event) => toggleCandidate(key, event.target.checked)} />
                            <span>{model.id}</span>
                            <small>{provider.name}</small>
                            <small>{isAnchor ? t.anchor : checked ? (locale === "en" ? "on" : "启用") : (locale === "en" ? "off" : "停用")}</small>
                          </label>
                        );
                      })}
                    </div>
                    <small>{form.selectionMode === "auto" ? t.candidatesHint : t.fixedHint}</small>
                    {form.selectionMode === "auto" && (
                      <div className="model-routing-parameters">
                        <label>
                          {t.exploration}
                          <input type="number" min="0" max="50" step="1" value={form.explorationPercent} onChange={(event) => setForm({ ...form, explorationPercent: Number(event.target.value) })} />
                        </label>
                        <label>
                          {t.latencyTarget}
                          <input type="number" min="1000" max="120000" step="1000" value={form.latencyTargetMs} onChange={(event) => setForm({ ...form, latencyTargetMs: Number(event.target.value) })} />
                        </label>
                        <label>
                          {t.failureCooldown}
                          <input type="number" min="5000" max="600000" step="5000" value={form.failureCooldownMs} onChange={(event) => setForm({ ...form, failureCooldownMs: Number(event.target.value) })} />
                        </label>
                      </div>
                    )}
                  </section>

	                  <section className="model-runtime-card">
                    <h4>{t.runtime}</h4>
                    <div className="model-runtime-fields">
                    <label>
                      {t.timeout}
                      <input
                        type="number"
                        min="1000"
                        max="120000"
                        value={form.timeoutMs}
                        onChange={(event) =>
                          setForm({ ...form, timeoutMs: Number(event.target.value) })
                        }
                      />
                    </label>
                    <label>
                      {t.challengeWait}
                      <input
                        type="number"
                        min="3000"
                        max="120000"
                        step="1000"
                        value={form.challengeWaitMs}
                        onChange={(event) =>
                          setForm({ ...form, challengeWaitMs: Number(event.target.value) })
                        }
                        aria-describedby="challenge-wait-hint"
                      />
                      <small id="challenge-wait-hint">{t.challengeWaitHint}</small>
                    </label>
                    <label>
                      {t.attempts}
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={form.maxChallengeAttempts}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            maxChallengeAttempts: Number(event.target.value),
                          })
                        }
                      />
                    </label>
                    <label>
                      {t.maxOutputTokens}
                      <input
                        type="number"
                        min={DEFAULT_MAX_OUTPUT_TOKENS}
                        max={MAX_MAX_OUTPUT_TOKENS}
                        step="1"
                        value={form.maxOutputTokens}
                        onChange={(event) =>
                          setForm({
                            ...form,
                            maxOutputTokens: Number(event.target.value),
                          })
                        }
                      />
                    </label>
                    </div>
                  </section>

                  <details className="model-advanced">
                    <summary>{t.advanced}</summary>
                    <div>
                      <label>
                        {t.delivery}
                        <select
                          value={form.deliveryMode}
                          onChange={(event) =>
                            setForm({
                              ...form,
                              deliveryMode: event.target.value as PresetForm["deliveryMode"],
                              selectionMode: event.target.value === "direct" ? "fixed" : form.selectionMode,
                            })
                          }
                        >
                          <option value="local_api">{t.local}</option>
                          <option value="direct">{t.direct}</option>
                        </select>
                      </label>
                      <label>
                        {t.transport}
                        <select
                          value={form.transport}
                          onChange={(event) => setForm({ ...form, transport: event.target.value })}
                        >
                          <option value="chat">{t.chat}</option>
                          <option value="gemini">{t.native}</option>
                        </select>
                      </label>
                      <label>
                        {t.endpoint}
                        <input
                          type="url"
                          value={form.endpointUrl}
                          onChange={(event) => setForm({ ...form, endpointUrl: event.target.value })}
                          placeholder="https://..."
                        />
                      </label>
                      <label>
                        {t.path}
                        <input
                          value={form.endpointPath}
                          onChange={(event) => setForm({ ...form, endpointPath: event.target.value })}
                        />
                      </label>
                      <label>
                        {t.key}
                        <input
                          type="password"
                          autoComplete="new-password"
                          value={form.apiKey}
                          onChange={(event) => setForm({ ...form, apiKey: event.target.value })}
                          placeholder={form.id ? "••••••••" : ""}
                        />
                      </label>
                    </div>
                  </details>

                  <div className="model-config-form-actions">
                    <div className="model-apply-scope">
                      <label className="model-apply-switch"><input type="checkbox" checked={applyAll} onChange={event => setApplyAll(event.target.checked)} />{t.applyAll}</label>
                      <small>{applyAll ? t.applyHint : t.defaultHint}</small>
                    </div>
                    <div className="model-save-buttons">
                    <button type="button" className="button button-outline" disabled={busy} onClick={cancelEdit}>
                      {t.cancel}
                    </button>
                    <button type="submit" className="button button-primary" disabled={busy}>
                      <Check size={15} />
                      {applyAll ? t.saveAll : t.saveDefault}
                    </button>
                    </div>
                  </div>
                </form>
              )}

              {!form && <section className="model-library">
                <div className="model-section-heading">
                  <div>
                    <h4>{t.library}</h4>
                    <p>
                      {t.libraryHint}
                    </p>
                  </div>
                  <button className="button button-primary" disabled={busy} onClick={newPreset}><Settings2 size={15} />{t.newPreset}</button>
                </div>
                <div className="model-config-list">
                  {snapshot.configs.map((config) => {
                    const workers = presetWorkers(config);
                    const blocked = workers.length ? `${t.presetInUse} ${workers.map(worker => worker.label).join(", ")}` : "";
                    return (
                    <article key={config.id} className="model-config-row">
                      <div className="model-config-icon">
                        <GitBranch size={16} />
                      </div>
                      <div className="model-config-main">
                        <strong>
                          {config.name}
                          {snapshot.defaultConfigId === config.id && (
                            <span>
                              <Star size={12} />
                              {t.default}
                            </span>
                          )}
                        </strong>
                        <small>{presetLabel(config, snapshot.providers)}</small>
                        <div className="model-selected-chips" aria-label={t.selectedModels}>
                          {candidateKeys(config).map(key => <span key={key}>{routeLabel(key, snapshot.providers)}</span>)}
                        </div>
                        <span>
                          {config.status === "active"
                            ? t.active
                            : config.status === "draft"
                              ? t.draft
                              : t.archived}{" "}
                          · v{config.version}
                        </span>
                        {blocked && <small className="model-delete-hint">{blocked}</small>}
                        <div className="model-routing-summary">
                          <span className={routingEnabled(config) ? "automatic" : "fixed"}>
                            {routingEnabled(config) ? t.automaticRouting : t.fixedRouting}
                          </span>
                          <small>{candidateCount(config)} {t.candidatesEnabled}</small>
                          {routingEnabled(config) && (
                            <>
                              <small>{t.exploration}: {Number(config.rules?.explorationPercent ?? 8)}%</small>
                              <small>{t.latencyTarget}: {Number(config.rules?.latencyTargetMs ?? 30000) / 1000}s</small>
                              <small>{t.failureCooldown}: {Number(config.rules?.failureCooldownMs ?? 90000) / 1000}s</small>
                            </>
                          )}
                          {config.status === "active" && (
                            <button
                              type="button"
                              className="button button-outline model-routing-toggle"
                              disabled={busy || (!routingEnabled(config) && config.deliveryMode !== "local_api")}
                              onClick={() => void updateRouting(config, { selectionMode: routingEnabled(config) ? "fixed" : "auto" })}
                            >
                              {routingEnabled(config) ? t.disableAutomatic : t.enableAutomatic}
                            </button>
                          )}
                        </div>
                        {config.status === "active" && (
                          <details className="model-scope-details">
                            <summary>{t.scope} · {candidateCount(config)}</summary>
                            <div className="model-scope-list">
                              {snapshot.providers.flatMap(provider => provider.models.map(model => ({ provider, model }))).map(({ provider, model }) => {
                                const key = routeKey(provider.id, model.id);
                                const enabledKeys = candidateKeys(config);
                                const isAnchor = provider.id === config.providerId && model.id === config.model;
                                const isEnabled = enabledKeys.includes(key);
                                return (
                                  <button
                                    key={key}
                                    type="button"
                                    className={isEnabled ? "active" : ""}
                                    aria-pressed={isEnabled}
                                    disabled={busy || isAnchor || (!isEnabled && enabledKeys.length >= 11) || (config.deliveryMode === "direct" && provider.id !== config.providerId)}
                                    onClick={() => void updateRouting(config, {
                                      comparisonModels: isEnabled
                                        ? enabledKeys.slice(1).filter(item => item !== key)
                                        : [...enabledKeys.slice(1), key].slice(0, 10),
                                    })}
                                  >
                                    <span>{model.id}</span>
                                    <small>{provider.name}</small>
                                    <small>{isAnchor ? t.anchor : isEnabled ? (locale === "en" ? "on" : "启用") : (locale === "en" ? "off" : "停用")}</small>
                                  </button>
                                );
                              })}
                            </div>
                          </details>
                        )}
                        {config.status === "active" && (
                          <div className="model-performance-list" aria-label={t.performance}>
                            {currentPerformance(config).map(({ model, stats }) => (
                              <span key={model} className={stats?.disabledUntil ? "cooldown" : ""}>
                                <strong>{routeLabel(model, snapshot.providers, config.providerId)}</strong>
                                <small title={t.taskSuccessRate}>{stats?.samples ? rate(stats.successRate) : t.noSamples}</small>
                                <small>{stats?.samples || 0} {locale === "en" ? "tasks" : "任务"}</small>
                                <small>{stats?.latencyEwmaMs ? `${Math.round(stats.latencyEwmaMs / 100) / 10}s` : "—"}</small>
                                {stats?.disabledUntil && <small>{locale === "en" ? "cooldown" : "熔断"}</small>}
                              </span>
                            ))}
                          </div>
                        )}
	                      </div>
                      <div className="model-config-actions">
                        <button className="button button-ghost model-delete-button" disabled={busy || workers.length > 0} title={blocked || `${t.deletePreset} ${config.name}`} aria-label={`${t.deletePreset} ${config.name}`} onClick={() => void deleteEntry("preset", config)}><Trash2 size={13} />{t.delete}</button>
                        <button className="button button-ghost" disabled={busy} onClick={() => editConfig(config)}>
                          <Edit3 size={13} />
                          {t.edit}
                        </button>
                        {config.status !== "active" && (
                          <button className="button button-primary" disabled={busy} onClick={() => void applyExisting(config)}>
                            <Rocket size={13} />
                            {t.publish}
                          </button>
                        )}
                        {config.status === "active" && snapshot.defaultConfigId !== config.id && (
                          <button className="button button-outline" disabled={busy} onClick={() => void setDefault(config)}>
                            <Star size={13} />
                            {t.setDefault}
                          </button>
                        )}
                        {config.status === "active" && snapshot.defaultConfigId !== config.id && (
                          <button className="button button-ghost" disabled={busy} onClick={() => void archive(config)}>
                            {t.archive}
                          </button>
                        )}
                      </div>
                    </article>
                  ); })}
                  {!snapshot.configs.length && <p className="model-config-empty">{t.noModels}</p>}
                </div>
              </section>}
              </section>
            </div>
          </section>
        </div>, document.body,
      )}
    </section>
  );
}
